import { Button } from "@mui/material";
import { isEqual, omit } from "lodash";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { useTranslation } from "react-i18next";
import { Drawer, toast } from "@outpave/ds";
import { useAddressData } from "hooks/useAddressData";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import {
  ConfigureAddressValidationType,
  FormikAddress,
} from "utils/addressFormat";
import ConfigureAddress from "components/common/ConfigureAddress";

interface ConfigureAddressDrawerProps {
  open: boolean;
  drawerTitle: string[];

  initialValues?: FormikAddress; // use this when address data is a part of a bigger whole (e.g., user)
  selectedAddressId?: string; // use this when fetching existing address data

  header?: string;
  subheader?: string;
  readOnly?: boolean;
  customAddressName?: boolean;
  customGapSize?: string;
  validationType?: ConfigureAddressValidationType;
  excludeFieldsOnSuccess?: boolean;
  onClose: () => void;
  onSuccess: (values: FormikAddress) => void;
  setReadOnly?: (readOnly: boolean) => void;
}

const ConfigureAddressDrawer: React.FC<ConfigureAddressDrawerProps> = (
  props,
) => {
  const {
    open,
    drawerTitle,
    header,
    subheader,
    readOnly = false,
    customAddressName = false,
    customGapSize,
    initialValues,
    selectedAddressId,
    validationType = ConfigureAddressValidationType.VALIDATE,
    excludeFieldsOnSuccess = true,
    onClose,
    onSuccess,
    setReadOnly,
  } = props;
  const { t } = useTranslation();

  const {
    formikAddress,
    specificLoadingStates,
    handleSaveAddress,
    resetFormToInitialValues,
  } = useAddressData({
    addressId: selectedAddressId,
    initialValues: initialValues,
    type: validationType,
  });

  const handleCloseDrawer = () => {
    resetFormToInitialValues();
    onClose();
  };

  const { validateAndTouch } = useFormikHelpers(formikAddress);

  const isSavingProfile =
    specificLoadingStates.creatingAddress ||
    specificLoadingStates.updatingAddress;

  // ConfigureAddress rewrites the name from the street unless the person names it.
  const changed = customAddressName
    ? formikAddress.dirty
    : !isEqual(
        omit(formikAddress.values, "addressName"),
        omit(formikAddress.initialValues, "addressName"),
      );
  const { requestLeave, dialog } = useUnsavedChangesGuard(
    changed,
    handleCloseDrawer,
  );

  const handleClose = () => {
    if (isSavingProfile) return;
    requestLeave();
  };

  const handleSave = async () => {
    if (validationType === ConfigureAddressValidationType.CREATE) {
      const success = await handleSaveAddress();
      if (!success) return;
      toast.success(
        selectedAddressId
          ? `${t("Location updated!")}`
          : `${t("Location added!")}`,
      );
      onSuccess(formikAddress.values);
      return;
    }
    let valuesToSend = formikAddress.values;
    if (excludeFieldsOnSuccess) {
      const { geocode, defaultLocation, ...rest } = formikAddress.values;
      valuesToSend = rest;
    }
    onSuccess(valuesToSend);
  };

  const isDefaultLocation = formikAddress.values?.defaultLocation;
  const subheaderToShow = isDefaultLocation
    ? t("DefaultCompanyLocationSubtitle")
    : subheader;

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        title={drawerTitle}
        loading={isSavingProfile}
        footer={
          <>
            <Button variant="default-soft" onClick={handleClose}>
              {t("Back")}
            </Button>
            {!readOnly && (
              <Button
                variant="primary-filled"
                disabled={isSavingProfile || !formikAddress.dirty}
                onClick={async () => {
                  const isValid = await validateAndTouch();
                  if (isValid) {
                    handleSave();
                  }
                }}
              >
                {t("Save")}
              </Button>
            )}
            {readOnly && setReadOnly && (
              <Button
                variant="secondary-filled"
                onClick={() => {
                  setReadOnly(false);
                }}
              >
                {t("Edit")}
              </Button>
            )}
          </>
        }
      >
        <ConfigureAddress
          title={header}
          subtitle={subheaderToShow}
          formik={formikAddress}
          customAddressName={customAddressName}
          readOnly={readOnly}
          customGapSize={customGapSize}
        />
      </Drawer>
      {dialog}
    </>
  );
};

export default ConfigureAddressDrawer;
